import { useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'

import { SectionHeading } from '~/components/SectionHeading'
import type { LandingEvent } from '~/api/types'
import { EventCard } from '~/components/EventCard'

import s from './FoldableSection.module.css'

/**
 * A dated window — Tonight, This week, the month — clipped to one row until it is unfolded.
 *
 * The clipping is CSS, not a slice of `events`: every card is in the `<ol>` at every width
 * and the stylesheet hides the ones past the first row. Nothing is added to or removed from
 * the DOM by opening, which is what lets the box be measured before and after and animated
 * between the two.
 *
 * The server-rendered page had a <noscript> block that unhid every card and hid both
 * controls, because there the gigs were in the HTML. Here they are not: with no JavaScript
 * there is no page at all, so there is nothing for a fallback to reveal and it is not
 * ported.
 */

/** How long the height takes, plus enough for a dropped `transitionend` to be noticed. */
const SETTLE_MS = 700

/**
 * `fetchpriority="high"` on the first section's opening cards, which are the page's largest
 * paint — but only on cards that are actually on screen.
 *
 * It was a flat six, which is the widest breakpoint's first row minus one. The fold shows
 * exactly one row and hides the rest with `display:none`, and a row is three cards at
 * 520–759px and four at 760–1039px — so on those widths two or three of the six were being
 * fetched at high priority into a subtree nothing can see. `display:none` stops a *lazy*
 * image loading; it does not stop an eager one, which is the whole point of marking it
 * eager. They were competing with the largest paint for bandwidth and could never be shown.
 *
 * So the count follows the row. Six rather than seven at the widest, because the Python
 * counted six and this is a port; the phone keeps two, where the row scrolls sideways and
 * the third card is a push away rather than behind a control.
 */
const EAGER_BY_WIDTH: [query: string, cards: number][] = [
  ['(min-width: 1040px)', 6],
  ['(min-width: 760px)', 4],
  ['(min-width: 520px)', 3],
]
const EAGER_NARROW = 2

/**
 * How many cards the first row holds at this width — the same four breakpoints the
 * stylesheet folds at, read live.
 *
 * Live rather than read once, because this decides whether the fold control is drawn at
 * all. A section with four cards has nothing to unfold at 1040px and everything to unfold
 * at 800px, and a reader who resizes across that should not be left with a control that
 * reveals nothing — which is what "more tonight" on a section showing all of tonight was.
 */
const ROWS: [query: string, cards: number][] = [
  ['(min-width: 1040px)', 7],
  ['(min-width: 760px)', 4],
  ['(min-width: 520px)', 3],
]

function rowSize(): number {
  if (typeof window === 'undefined' || !window.matchMedia) return 7
  for (const [query, cards] of ROWS) {
    if (window.matchMedia(query).matches) return cards
  }
  // Under 520px the strip scrolls sideways instead of folding, so every card is reachable
  // and there is nothing to unfold however many there are.
  return Number.POSITIVE_INFINITY
}

function useRowSize(): number {
  const [size, setSize] = useState(rowSize)
  useEffect(() => {
    if (!window.matchMedia) return
    const watching = ROWS.map(([query]) => window.matchMedia(query))
    const seen = () => setSize(rowSize())
    for (const mql of watching) mql.addEventListener('change', seen)
    return () => {
      for (const mql of watching) mql.removeEventListener('change', seen)
    }
  }, [])
  return size
}

function eagerCards(): number {
  // The widest breakpoint, which is what a test and any non-browser render get. Guessing
  // high here is the safe direction: the fallback only ever runs where there is no CSS to
  // hide anything.
  if (typeof window === 'undefined' || !window.matchMedia) return 6
  for (const [query, cards] of EAGER_BY_WIDTH) {
    if (window.matchMedia(query).matches) return cards
  }
  return EAGER_NARROW
}

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)'

export interface FoldableSectionProps {
  /** The section's own anchor — `tonight`, `week`, `month`. The list takes `s-{id}`, which
   *  is what both controls point `aria-controls` at. */
  id: string
  heading: string
  /** The nights the window covers, already worded. */
  meta: string
  events: LandingEvent[]
  /** Whether the calendar holds more than this window shows. Never how many: a count of
   *  what is being withheld is a fact about the selection rather than about tonight. */
  hasMore: boolean
  moreHref: string
  /** What the seam says instead, when nothing is being withheld. */
  doneText: string
  /** The first section on the page only. */
  eager?: boolean
  /**
   * Lead the section with its first gig at full width, on a phone.
   *
   * For the window a reader has come for. A row of half-width cards gives tonight's gig the
   * same weight as the fourth one in a fortnight, and on a phone it gives it a third of the
   * screen's width — so the page opens on three cropped thumbnails rather than on a night
   * out. The rest of the window keeps the grid; it is only the first that leads.
   *
   * Phone only: above that width the row already fits three or four cards without cropping
   * any of them, and a hero there would be a banner.
   */
  lead?: boolean
  /**
   * Drop the section's own side gutter, for a page that already has one.
   *
   * It pads itself because the front page sets it straight into a bare `<main>`. The search
   * results put it inside a padded wrapper alongside three sections that do not pad — so it
   * alone sat a gutter further in, and the gigs did not line up with the acts under them.
   *
   * A prop rather than a rule in the other page's stylesheet reaching in at `section`: this
   * component's classes are scoped and are not a public API, and a selector that depends on
   * which element it happens to render is one that breaks silently when it renders another.
   */
  flush?: boolean
}

export function FoldableSection({
  flush,
  id,
  heading,
  meta,
  events,
  hasMore,
  moreHref,
  doneText,
  eager = false,
  lead = false,
}: FoldableSectionProps) {
  /*
   * Read once, on mount. Resizing across a breakpoint after the page has painted does not
   * want re-deciding: the images those cards need are already fetched, and `eager` only
   * ever describes the first paint.
   */
  const [firstRow] = useState(eagerCards)
  // Live, unlike `firstRow`: it decides both whether there is anything to unfold and
  // which card is the first one unfolding reveals, and a reader who has resized the
  // window since mount is looking at the new row.
  const rowSize = useRowSize()
  // Only worth a control if there is something behind it.
  const foldable = events.length > rowSize
  const [open, setOpen] = useState(false)
  /**
   * Mid-flight the section is neither shut nor open: the box is a fixed height that is
   * moving, and every card has to be rendered inside it while the nth-child rules that
   * would hide the later rows are suspended. So this is its own state and not derived from
   * `open`.
   */
  const [anim, setAnim] = useState(false)

  const headRef = useRef<HTMLDivElement>(null)
  const stripRef = useRef<HTMLOListElement>(null)

  /* A second click while the height is still moving would measure a box that is halfway to
     somewhere and animate away from that. Dropped. */
  const busy = useRef(false)
  const guard = useRef<number | null>(null)
  const release = useRef<(() => void) | null>(null)

  useEffect(
    () => () => {
      if (guard.current !== null) window.clearTimeout(guard.current)
      release.current?.()
    },
    [],
  )

  const listId = `s-${id}`

  /* Folding up must not drop the reader below the section they just closed. */
  function keepPlace(reduced: boolean) {
    const head = headRef.current
    if (!head) return
    const top = head.getBoundingClientRect().top
    if (top >= 0) return
    window.scrollTo({ top: top + window.scrollY - 14, behavior: reduced ? 'auto' : 'smooth' })
  }

  function finish() {
    if (guard.current !== null) {
      window.clearTimeout(guard.current)
      guard.current = null
    }
    release.current?.()
    release.current = null
    /* The class and the inline height have to go in the same frame. `.anim` is what
       suspends the hiding, so a paint taken between them is a paint of every card at its
       natural height — the flash the measured animation exists to avoid. */
    flushSync(() => setAnim(false))
    const strip = stripRef.current
    if (strip) strip.style.height = ''
    busy.current = false
  }

  /**
   * Where the keyboard goes when the section opens.
   *
   * The cards it reveals sit *before* the seam button in the document, so a reader who
   * pressed that button and then tabbed forward walked straight out of the section and past
   * every card they had just asked for. The only way back was Shift+Tab, which is not a
   * thing anybody should have to work out.
   *
   * The first revealed card, not the first card: the ones already on screen were reachable
   * before the press and sending focus back to them would lose a reader's place rather than
   * keep it. `rowSize` is how many were showing, so that index is the first new one — and
   * it is the live count rather than `firstRow`, which froze at mount to decide `eager` and
   * is a different question.
   *
   * Nothing happens on the way shut. The button is still under the pointer and still under
   * the keyboard, and `keepPlace` is already holding the scroll position steady.
   */
  function focusFirstRevealed() {
    const strip = stripRef.current
    if (!strip) return
    const revealed = strip.children[rowSize]
    // Not the first anchor in the card: that is the picture, which carries `aria-hidden`
    // and `tabindex="-1"` precisely so the keyboard skips it and lands on the act's name
    // instead. Focusing it would put a reader on an element the accessibility tree says is
    // not there.
    const target = revealed?.querySelector<HTMLElement>(
      'a[href]:not([aria-hidden="true"]):not([tabindex="-1"]), button:not([disabled])',
    )
    // Without preventScroll the browser jumps the card to the top of the window, undoing
    // the work `keepPlace` and `rise` do to make the section grow in place.
    target?.focus({ preventScroll: true })
  }

  function toggle() {
    const strip = stripRef.current
    if (busy.current || !strip) return

    const next = !open
    const reduced = window.matchMedia(REDUCED_MOTION).matches
    if (reduced) {
      // Flushed, so the revealed cards are in the document before focus goes looking for
      // one. `setOpen` on its own is still pending when the next line runs.
      flushSync(() => setOpen(next))
      if (next) focusFirstRevealed()
      else keepPlace(reduced)
      return
    }

    busy.current = true
    const h0 = strip.getBoundingClientRect().height
    /* Both heights have to be real layout, so the state change is flushed rather than
       batched: React would otherwise still be showing the old row count at the second
       read, and the box would animate from a height to itself. */
    flushSync(() => setOpen(next))
    const h1 = strip.getBoundingClientRect().height
    if (!next) keepPlace(reduced)

    /* While it moves, every card stays rendered and the box does the clipping. */
    flushSync(() => setAnim(true))
    strip.style.height = `${h0}px`
    void strip.offsetHeight // the browser has to have seen h0, or there is nothing to move from
    strip.style.height = `${h1}px`

    if (next) {
      rise(strip)
      focusFirstRevealed()
    }

    guard.current = window.setTimeout(finish, SETTLE_MS)
    const ended = (event: TransitionEvent) => {
      if (event.target !== strip || event.propertyName !== 'height') return
      finish()
    }
    strip.addEventListener('transitionend', ended)
    release.current = () => strip.removeEventListener('transitionend', ended)
  }

  return (
    <section
      className={cls(s.sec, s.foldable, Boolean(flush) && s.flush, open && s.open, anim && s.anim)}
      id={id}
    >
      <div className={s.head} ref={headRef}>
        <SectionHeading>{heading}</SectionHeading>
        <div className={s.headR}>
          <span className={cls(s.meta, s.mono)}>{meta}</span>
          {/* Two controls, one section. Both carry the state, because a reader who reaches
              either one is asking the same list the same question — and both are absent
              when there is nothing behind them. */}
          {foldable ? (
          <button
            className={cls(s.foldTop, s.mono)}
            type="button"
            aria-expanded={open}
            aria-controls={listId}
            onClick={toggle}
          >
            <span className={s.chev} aria-hidden="true">
              &#9662;
            </span>
            fold
          </button>
          ) : null}
        </div>
      </div>

      <ol className={cls(s.strip, anim && s.anim, lead && s.lead)} id={listId} ref={stripRef}>
        {events.map((event, n) => (
          <EventCard key={event.id} event={event} eager={eager && n < firstRow} />
        ))}
      </ol>

      {/*
        * The seam goes entirely when it has nothing to say: no fold, because everything is
        * already on screen, and no "the calendar has the rest", because it does not. A rule
        * with a control on it that reveals nothing is worse than no rule.
        */}
      {foldable || hasMore ? (
      <div className={s.seam}>
        {foldable ? (
        <button
          className={cls(s.fold, s.mono)}
          type="button"
          aria-expanded={open}
          aria-controls={listId}
          onClick={toggle}
        >
          <span className={cls(s.lbl, s.lblShut)}>more {heading.toLowerCase()}</span>
          <span className={cls(s.lbl, s.lblOpen)}>fold back up</span>
          <span className={s.chev} aria-hidden="true">
            &#9662;
          </span>
        </button>
        ) : null}
        {hasMore ? (
          <a className={cls(s.seamNote, s.more, s.mono)} href={moreHref}>
            the calendar has the rest{' '}
            <span className={s.arrow} aria-hidden="true">
              &#8594;
            </span>
          </a>
        ) : (
          <span className={cls(s.seamNote, s.done, s.mono)}>{doneText}</span>
        )}
      </div>
      ) : null}
    </section>
  )
}

/** The rows that were already there rise into the space the box is making. */
function rise(strip: HTMLOListElement) {
  const items = Array.from(strip.children) as HTMLElement[]
  const lead = items[0]
  /* Web Animations, or nothing: the fold still works without it and this is decoration. */
  if (!lead || typeof lead.animate !== 'function') return
  const firstRow = lead.offsetTop
  items
    .filter(item => item.offsetTop > firstRow)
    .forEach((item, i) => {
      item.animate(
        [
          { opacity: 0, transform: 'translateY(12px)' },
          { opacity: 1, transform: 'none' },
        ],
        {
          duration: 450,
          delay: Math.min(i * 24, 260),
          easing: 'cubic-bezier(.22,.61,.36,1)',
          fill: 'backwards',
        },
      )
    })
}

function cls(...names: (string | false)[]) {
  return names.filter(Boolean).join(' ')
}
